import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function VoiceAIPage() {
  const [leads, setLeads] = useState('50');
  const [closeRate, setCloseRate] = useState('20');
  const [avgValue, setAvgValue] = useState('5000');

  const calculateROI = () => {
    const leadsNum = parseFloat(leads) || 0;
    const closeRateNum = parseFloat(closeRate) || 0;
    const avgValueNum = parseFloat(avgValue) || 0;
    
    const currentRevenue = leadsNum * (closeRateNum / 100) * avgValueNum;
    const improvedCloseRate = closeRateNum * 1.4;
    const improvedRevenue = leadsNum * (improvedCloseRate / 100) * avgValueNum;
    const additionalRevenue = improvedRevenue - currentRevenue;
    
    return {
      current: currentRevenue.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }),
      improved: improvedRevenue.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }),
      additional: additionalRevenue.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
    };
  };

  const roi = calculateROI();

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <section className="relative overflow-hidden bg-white grid-bg pt-36 pb-20">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse at 50% 0%, rgba(13,148,136,0.14), transparent 58%), radial-gradient(ellipse at 85% 20%, rgba(249,115,22,0.10), transparent 55%)' }}
        ></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center flex flex-col items-center">
            <div className="eyebrow"><i className="ri-customer-service-2-line"></i><span>Never Miss Another Lead</span></div>
            <h1 className="mt-6 h-display text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-[-0.02em]">
              AI Voice Agents
            </h1>
            <p className="mt-6 text-lg md:text-xl text-slate-600 font-body font-light max-w-2xl">
              Answer every call, qualify every lead, book every appointment — 24/7, instantly.
            </p>
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-4xl mx-auto mb-20">
            <h2 className="text-4xl font-bold font-display text-navy-900 mb-8">
              The Problem: Missed Opportunities
            </h2>

            <div className="grid md:grid-cols-3 gap-6 mb-12">
              <div className="bg-navy-50 border-2 border-navy-100 rounded-xl p-6">
                <div className="w-12 h-12 flex items-center justify-center bg-teal-50 rounded-lg mb-4">
                  <i className="ri-phone-line text-teal-600 text-2xl"></i>
                </div>
                <h3 className="text-xl font-bold text-navy-900 mb-2">Missed Calls</h3>
                <p className="text-navy-600 text-sm leading-relaxed">
                  You're on a job, in a meeting, or after hours. Calls go to voicemail. Leads go to competitors.
                </p>
              </div>

              <div className="bg-navy-50 border-2 border-navy-100 rounded-xl p-6">
                <div className="w-12 h-12 flex items-center justify-center bg-teal-50 rounded-lg mb-4">
                  <i className="ri-time-line text-teal-600 text-2xl"></i>
                </div>
                <h3 className="text-xl font-bold text-navy-900 mb-2">Slow Response</h3>
                <p className="text-navy-600 text-sm leading-relaxed">
                  By the time you call back, they've already contacted 3 other companies. You're too late.
                </p>
              </div>

              <div className="bg-navy-50 border-2 border-navy-100 rounded-xl p-6">
                <div className="w-12 h-12 flex items-center justify-center bg-teal-50 rounded-lg mb-4">
                  <i className="ri-moon-line text-teal-600 text-2xl"></i>
                </div>
                <h3 className="text-xl font-bold text-navy-900 mb-2">After-Hours Loss</h3>
                <p className="text-navy-600 text-sm leading-relaxed">
                  Emergencies and urgent needs happen at night and weekends. You're losing high-value jobs.
                </p>
              </div>
            </div>

            <div className="bg-teal-50 border-2 border-teal-200 rounded-2xl p-8">
              <h3 className="text-2xl font-bold font-display text-navy-900 mb-4">The 5-Minute Rule</h3>
              <p className="text-base text-navy-700 leading-relaxed mb-4">
                Studies show that responding to a lead within 5 minutes increases conversion rates by up to 400% compared to responding after 30 minutes.
              </p>
              <p className="text-base text-navy-700 leading-relaxed">
                <strong>AI voice agents respond in seconds, not hours.</strong> They never sleep, never take breaks, and never let a lead slip away.
              </p>
            </div>
          </div>

          <div className="mb-20">
            <h2 className="text-3xl font-bold font-display text-navy-900 mb-8 text-center">
              How AI Voice Agents Work
            </h2>

            <div className="bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900 text-white rounded-2xl p-12">
              <div className="grid md:grid-cols-6 gap-4">
                <div className="text-center">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-teal-500 rounded-full">
                    <i className="ri-phone-line text-navy-900 text-2xl"></i>
                  </div>
                  <h4 className="font-bold mb-2 text-sm">1. Inbound Call</h4>
                  <p className="text-xs text-navy-100">Customer calls your number</p>
                </div>

                <div className="flex items-center justify-center">
                  <i className="ri-arrow-right-line text-orange-400 text-2xl"></i>
                </div>

                <div className="text-center">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-teal-500 rounded-full">
                    <i className="ri-robot-line text-navy-900 text-2xl"></i>
                  </div>
                  <h4 className="font-bold mb-2 text-sm">2. AI Answers</h4>
                  <p className="text-xs text-navy-100">Natural conversation begins instantly</p>
                </div>

                <div className="flex items-center justify-center">
                  <i className="ri-arrow-right-line text-orange-400 text-2xl"></i>
                </div>

                <div className="text-center">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-teal-500 rounded-full">
                    <i className="ri-question-answer-line text-navy-900 text-2xl"></i>
                  </div>
                  <h4 className="font-bold mb-2 text-sm">3. Qualification</h4>
                  <p className="text-xs text-navy-100">AI asks key questions, scores lead</p>
                </div>

                <div className="flex items-center justify-center">
                  <i className="ri-arrow-right-line text-orange-400 text-2xl"></i>
                </div>

                <div className="text-center">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-teal-500 rounded-full">
                    <i className="ri-calendar-check-line text-navy-900 text-2xl"></i>
                  </div>
                  <h4 className="font-bold mb-2 text-sm">4. Booking</h4>
                  <p className="text-xs text-navy-100">Schedules appointment directly</p>
                </div>
              </div>

              <div className="mt-8 pt-8 border-t border-navy-600 grid md:grid-cols-2 gap-6">
                <div className="flex items-start space-x-3">
                  <i className="ri-database-line text-orange-400 text-2xl flex-shrink-0"></i>
                  <div>
                    <h4 className="font-bold mb-1 text-sm">5. CRM Logging</h4>
                    <p className="text-xs text-navy-100">All details automatically saved to your CRM</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <i className="ri-mail-send-line text-orange-400 text-2xl flex-shrink-0"></i>
                  <div>
                    <h4 className="font-bold mb-1 text-sm">6. Follow-Up</h4>
                    <p className="text-xs text-navy-100">Automated reminders and nurture sequences</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mb-20">
            <h2 className="text-3xl font-bold font-display text-navy-900 mb-8 text-center">
              Voice AI ROI Calculator
            </h2>

            <div className="bg-navy-50 rounded-2xl p-8 border-2 border-navy-100">
              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <div>
                  <label className="block text-sm font-semibold text-navy-700 mb-2">
                    Leads per Month
                  </label>
                  <input
                    type="number"
                    value={leads}
                    onChange={(e) => setLeads(e.target.value)}
                    className="w-full px-4 py-3 border border-navy-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-navy-700 mb-2">
                    Current Close Rate (%)
                  </label>
                  <input
                    type="number"
                    value={closeRate}
                    onChange={(e) => setCloseRate(e.target.value)}
                    className="w-full px-4 py-3 border border-navy-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-navy-700 mb-2">
                    Average Job Value ($)
                  </label>
                  <input
                    type="number"
                    value={avgValue}
                    onChange={(e) => setAvgValue(e.target.value)}
                    className="w-full px-4 py-3 border border-navy-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-6">
                <div className="bg-white rounded-xl p-6 text-center border-2 border-navy-100">
                  <p className="text-sm text-navy-600 mb-2">Current Monthly Revenue</p>
                  <p className="text-3xl font-bold text-navy-900">{roi.current}</p>
                </div>
                <div className="bg-gradient-to-br from-navy-900 to-navy-800 rounded-xl p-6 text-center text-white">
                  <p className="text-sm text-navy-100 mb-2">With Voice AI (40% improvement)</p>
                  <p className="text-3xl font-bold text-orange-400">{roi.improved}</p>
                </div>
                <div className="bg-teal-50 border-2 border-teal-200 rounded-xl p-6 text-center">
                  <p className="text-sm text-teal-700 mb-2">Additional Monthly Revenue</p>
                  <p className="text-3xl font-bold text-teal-700">{roi.additional}</p>
                </div>
              </div>

              <p className="text-sm text-navy-600 text-center mt-6">
                Based on industry average 40% conversion improvement from instant response
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-20">
            <div className="bg-white border-2 border-navy-100 rounded-xl p-8">
              <h3 className="text-2xl font-bold font-display text-navy-900 mb-6">What Voice AI Can Do:</h3>
              <ul className="space-y-3">
                <li className="flex items-start space-x-3">
                  <i className="ri-check-line text-teal-600 text-xl flex-shrink-0 mt-0.5"></i>
                  <span className="text-navy-700 text-sm">Answer calls 24/7, never miss an opportunity</span>
                </li>
                <li className="flex items-start space-x-3">
                  <i className="ri-check-line text-teal-600 text-xl flex-shrink-0 mt-0.5"></i>
                  <span className="text-navy-700 text-sm">Qualify leads with custom questions</span>
                </li>
                <li className="flex items-start space-x-3">
                  <i className="ri-check-line text-teal-600 text-xl flex-shrink-0 mt-0.5"></i>
                  <span className="text-navy-700 text-sm">Schedule appointments directly into your calendar</span>
                </li>
                <li className="flex items-start space-x-3">
                  <i className="ri-check-line text-teal-600 text-xl flex-shrink-0 mt-0.5"></i>
                  <span className="text-navy-700 text-sm">Handle multiple calls simultaneously</span>
                </li>
                <li className="flex items-start space-x-3">
                  <i className="ri-check-line text-teal-600 text-xl flex-shrink-0 mt-0.5"></i>
                  <span className="text-navy-700 text-sm">Log everything to your CRM automatically</span>
                </li>
                <li className="flex items-start space-x-3">
                  <i className="ri-check-line text-teal-600 text-xl flex-shrink-0 mt-0.5"></i>
                  <span className="text-navy-700 text-sm">Send follow-up messages and reminders</span>
                </li>
                <li className="flex items-start space-x-3">
                  <i className="ri-check-line text-teal-600 text-xl flex-shrink-0 mt-0.5"></i>
                  <span className="text-navy-700 text-sm">Handle common objections naturally</span>
                </li>
                <li className="flex items-start space-x-3">
                  <i className="ri-check-line text-teal-600 text-xl flex-shrink-0 mt-0.5"></i>
                  <span className="text-navy-700 text-sm">Transfer to human when needed</span>
                </li>
              </ul>
            </div>

            <div className="bg-white border-2 border-navy-100 rounded-xl p-8">
              <h3 className="text-2xl font-bold font-display text-navy-900 mb-6">Perfect For:</h3>
              <ul className="space-y-3">
                <li className="flex items-start space-x-3">
                  <i className="ri-tools-line text-teal-600 text-xl flex-shrink-0 mt-0.5"></i>
                  <span className="text-navy-700 text-sm"><strong>Home Services:</strong> Capture emergency calls after hours</span>
                </li>
                <li className="flex items-start space-x-3">
                  <i className="ri-briefcase-line text-teal-600 text-xl flex-shrink-0 mt-0.5"></i>
                  <span className="text-navy-700 text-sm"><strong>Professional Services:</strong> Qualify and schedule consultations</span>
                </li>
                <li className="flex items-start space-x-3">
                  <i className="ri-home-line text-teal-600 text-xl flex-shrink-0 mt-0.5"></i>
                  <span className="text-navy-700 text-sm"><strong>Real Estate:</strong> Book property tours instantly</span>
                </li>
                <li className="flex items-start space-x-3">
                  <i className="ri-hospital-line text-teal-600 text-xl flex-shrink-0 mt-0.5"></i>
                  <span className="text-navy-700 text-sm"><strong>Healthcare:</strong> Schedule appointments 24/7</span>
                </li>
                <li className="flex items-start space-x-3">
                  <i className="ri-store-line text-teal-600 text-xl flex-shrink-0 mt-0.5"></i>
                  <span className="text-navy-700 text-sm"><strong>Local Businesses:</strong> Never miss a customer inquiry</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold font-display text-white mb-6">
            See Voice AI in Action
          </h2>
          <p className="text-xl text-navy-100 mb-8">
            Book a demo and hear how natural our AI voice agents sound
          </p>
          <a
            href="https://floridadigitalmarketingexperts.com/demos/red-apple-roofing"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary inline-flex items-center px-8 py-4 whitespace-nowrap cursor-pointer"
          >
            Book a Voice AI Demo
            <i className="ri-arrow-right-line ml-2"></i>
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
